import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";

const HASHTAG_RE = /^#[a-z0-9]{1,32}$/;

const TrackInfoEditor = ({ track }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState(null);
    const [meta, setMeta] = useState(null);
    const [form, setForm] = useState({ name: "", subhead: "", hashtag: "" });

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const snap = await getDoc(doc(db, "tracks", track));
                if (cancelled) return;
                if (!snap.exists()) {
                    setError("Track config missing in Firestore.");
                    return;
                }
                const data = snap.data() || {};
                setMeta(data);
                setForm({
                    name: data.name || "",
                    subhead: data.subhead || "",
                    hashtag: data.hashtag || "",
                });
            } catch (err) {
                if (!cancelled) setError("Failed to load track: " + err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [track]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setStatus(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setError(null);
        setStatus(null);
        if (!form.name.trim()) {
            setError("Name is required.");
            return;
        }
        if (!HASHTAG_RE.test(form.hashtag)) {
            setError("Hashtag must be lowercase alphanumeric, starting with #, max 32 chars (e.g. #hackaton).");
            return;
        }
        setSaving(true);
        try {
            await updateDoc(doc(db, "tracks", track), {
                name: form.name.trim(),
                subhead: form.subhead.trim(),
                hashtag: form.hashtag.trim(),
            });
            setStatus("Saved.");
            setMeta((prev) => ({ ...(prev || {}), ...form }));
        } catch (err) {
            setError("Save failed: " + err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="text-sm text-gray-500">Loading track info...</div>;

    return (
        <div className="w-full bg-white rounded-lg p-6 border border-gray-200">
            <h2 className="text-xl font-semibold mb-4 text-left">Track info</h2>
            <hr className="mb-4" />
            <form onSubmit={handleSave} className="flex flex-col gap-4">
                <div className="flex flex-col">
                    <label className="text-left font-semibold mb-1">Name</label>
                    <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        className="border border-gray-200 rounded-xl p-3"
                        placeholder="HackATon"
                    />
                </div>
                <div className="flex flex-col">
                    <label className="text-left font-semibold mb-1">Hashtag</label>
                    <input
                        type="text"
                        name="hashtag"
                        value={form.hashtag}
                        onChange={handleChange}
                        className="border border-gray-200 rounded-xl p-3"
                        placeholder="#hackaton"
                    />
                </div>
                <div className="flex flex-col">
                    <label className="text-left font-semibold mb-1">Subhead</label>
                    <textarea
                        name="subhead"
                        value={form.subhead}
                        onChange={handleChange}
                        rows={3}
                        className="border resize-none border-gray-200 rounded-xl p-3"
                        placeholder="Short description shown under the H1 on the feed."
                    />
                </div>
                {error && <p className="text-red-600 text-sm text-left">{error}</p>}
                {status && <p className="text-green-700 text-sm text-left">{status}</p>}
                <button
                    type="submit"
                    disabled={saving}
                    className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl p-2 disabled:opacity-50 self-start px-6"
                >
                    {saving ? "Saving..." : "Save"}
                </button>
            </form>
            {meta && (
                <div className="mt-5 pt-4 border-t border-gray-100 text-xs text-gray-500 grid grid-cols-3 gap-2">
                    <div>Slug · <span className="text-gray-800 font-medium">{track}</span></div>
                    <div>Active · <span className="text-gray-800 font-medium">{String(meta.active !== false)}</span></div>
                    <div>Position · <span className="text-gray-800 font-medium">{meta.position ?? "—"}</span></div>
                </div>
            )}
        </div>
    );
};

export default TrackInfoEditor;
